import React from 'react';
import BiometricEnrollment from '../../components/admin/BiometricEnrollment';
import Badge from '../../components/ui/Badge';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table';
import { useAdminData } from '../../context/AdminDataContext';
import { useRole } from '../../context/RoleContext';
import { listClientEmployees } from '../../lib/api/client';
import { ApiEmployee } from '../../lib/api/contracts';
import { toEmployeeViewModel } from '../../lib/api/adapters';

const statusBadgeVariant = (status: ApiEmployee['employee_status']): 'success' | 'warning' | 'error' => {
  if (status === 'ACTIVE') {
    return 'success';
  }
  if (status === 'INACTIVE') {
    return 'warning';
  }
  return 'error';
};

const UserManagement: React.FC = () => {
  const { activeCompanyId } = useRole();
  const { clients } = useAdminData();
  const [employees, setEmployees] = React.useState<ApiEmployee[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const activeClient = clients.find((client) => client.id === activeCompanyId) ?? clients[0] ?? null;
  const [enrollmentId, setEnrollmentId] = React.useState<string | null>(null);
  React.useEffect(() => { setEnrollmentId(null); setEmployees([]); }, [activeClient?.id]);
  const employeeRows = employees.map(toEmployeeViewModel);

  React.useEffect(() => {
    if (!activeClient?.id) {
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    listClientEmployees({ client_id: activeClient.id })
      .then((rows) => {
        if (mounted) {
          setEmployees(rows);
        }
      })
      .catch((err) => {
        if (mounted) {
          setEmployees([]);
          setError(err instanceof Error ? err.message : 'Employee API unavailable.');
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [activeClient?.id]);

  return (
    <div className="space-y-6">
      <section className="admin-page-header">
        <div>
          <h1 className="text-3xl font-semibold">Workforce</h1>
          <p className="mt-1 text-sm text-[var(--admin-muted)]">
            Employee roster surface for {activeClient?.name ?? 'the selected client'}.
          </p>
        </div>
      </section>

      {enrollmentId && activeClient ? <BiometricEnrollment key={`${activeClient.id}:${enrollmentId}`} employeeId={enrollmentId} clientId={activeClient.id} onClose={() => setEnrollmentId(null)} /> : null}
      <Card>
        <CardHeader>
          <CardTitle>{error ? 'Module Unavailable' : 'Employee Roster'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-[var(--admin-muted)]">
          {error ? (
            <>
              <p>Employee data could not be loaded from `POST /admin/api/listClientEmployees`.</p>
              <div className="rounded-2xl border border-[var(--admin-amber)] bg-[var(--admin-amber-soft)] px-4 py-3">
                API response: {error}
              </div>
            </>
          ) : null}

          {!error && loading ? <p>Loading employee roster...</p> : null}

          {!error && !loading && employeeRows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--admin-border)] px-4 py-10 text-center">
              No employees returned for this client.
            </div>
          ) : null}

          {!error && employeeRows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Hire Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Clock State</TableHead><TableHead>Face enrollment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employeeRows.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell className="font-mono text-xs">{employee.employeeCode}</TableCell>
                    <TableCell className="font-semibold">{employee.name || employee.id}</TableCell>
                    <TableCell>{employee.email}</TableCell>
                    <TableCell>{employee.phone}</TableCell>
                    <TableCell>{employee.hireDate}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(employee.status)}>{employee.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={employee.isClockedIn ? 'success' : 'outline'}>
                        {employee.isClockedIn ? 'Clocked in' : 'Clocked out'}
                      </Badge>
                    </TableCell>
                    <TableCell><button disabled={loading || employee.status !== 'ACTIVE'} className="rounded-lg border border-[var(--admin-border)] px-3 py-2 disabled:opacity-40" onClick={() => setEnrollmentId(employee.id)}>Manage face</button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
